import { DEPLOYMENT_PROFILES } from "@pinpoint/site-routes/deployment-profile";

import candidateFamilyRouteManifest from "./candidateFamilyRouteManifest";
import candidateLearnRouteManifest from "./candidateLearnRouteManifest";
import candidateProductShellRouteManifest from "./candidateProductShellRouteManifest";
import candidateSatRouteManifest from "./candidateSatRouteManifest";
import familyRouteManifest from "./familyRouteManifest";
import learnRouteManifest from "./learnRouteManifest";
import legacyAppRouteManifest from "./legacyAppRouteManifest";
import nextRouteManifest from "./nextRouteManifest";

export const ROUTE_CLASSIFICATIONS = {
  MARKETING: "marketing",
  PRODUCT: "product",
  RETIRED: "retired",
  UNKNOWN: "unknown",
} as const;

const candidateProfile = {
  profile: DEPLOYMENT_PROFILES.WWW_CANDIDATE,
  shell: candidateProductShellRouteManifest,
  sat: candidateSatRouteManifest,
  family: candidateFamilyRouteManifest,
  learn: candidateLearnRouteManifest,
};

const legacyProfile = {
  profile: DEPLOYMENT_PROFILES.LEGACY_APP,
  shell: legacyAppRouteManifest,
  sat: nextRouteManifest,
  family: familyRouteManifest,
  learn: learnRouteManifest,
};

export function selectProductRouteProfile(profile: string) {
  if (profile === DEPLOYMENT_PROFILES.LEGACY_APP) return legacyProfile;
  if (profile === DEPLOYMENT_PROFILES.WWW_CANDIDATE) return candidateProfile;
  throw new Error(`Unknown deployment profile: ${profile}`);
}

function flattenRoutes(routes: any[]): any[] {
  return routes.flatMap((route) => [route, ...(route.children ? flattenRoutes(route.children) : [])]);
}

function matchesPath(pattern: string, pathname: string) {
  if (pattern === "/*") return pathname.startsWith("/");
  if (pattern.endsWith("/*")) {
    const base = pattern.slice(0, -2);
    return pathname === base || pathname.startsWith(`${base}/`);
  }
  const patternParts = pattern.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);
  if (pattern === "/") return pathname === "/";
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, index) => part.startsWith(":") || part === pathParts[index]);
}

function findMountedRoute(mount: string, routes: any[], pathname: string) {
  if (pathname === mount) return routes.find((route) => route.index);
  return routes.find((route) => (
    route.path && route.path !== "*" && matchesPath(`${mount}/${route.path}`, pathname)
  ));
}

const retiredManifestRoutes = [
  ...flattenRoutes(nextRouteManifest)
    .filter((route) => route.path && route.path !== "*")
    .map((route) => `/${route.path}`),
  ...flattenRoutes(legacyAppRouteManifest)
    .filter((route) => route.component === "LegacySatRedirect")
    .map((route) => route.path),
  "/login",
  "/access-pending",
  "/choose-role",
  "/unavailable",
  "/offline",
  "/next/app/*",
  "/learn/library",
  "/sat/kg-mock/*",
  "/sat/admin",
];

function isRetiredCandidatePath(pathname: string) {
  return retiredManifestRoutes.some((pattern) => matchesPath(pattern, pathname));
}

function findCandidateProductRoute(pathname: string) {
  const profile = candidateProfile;
  const shellProduct = profile.shell.find(
    (route: any) => route.path && !route.path.endsWith("/*") && matchesPath(route.path, pathname),
  );
  return shellProduct
    ?? findMountedRoute("/sat", profile.sat, pathname)
    ?? findMountedRoute("/family", profile.family, pathname)
    ?? findMountedRoute("/learn", profile.learn, pathname);
}

function findLegacyProductRoute(pathname: string) {
  const routes = flattenRoutes(legacyProfile.shell);
  return routes.find((route) => route.path && route.path !== "/*" && matchesPath(route.path, pathname))
    ?? routes.find((route) => route.path === "/*");
}

export function classifyBeforeAuth(profile: string, pathname: string) {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) {
    return { kind: ROUTE_CLASSIFICATIONS.UNKNOWN } as const;
  }

  if (profile === DEPLOYMENT_PROFILES.LEGACY_APP) {
    const route = findLegacyProductRoute(pathname);
    return route
      ? { kind: ROUTE_CLASSIFICATIONS.PRODUCT, routeId: route.id } as const
      : { kind: ROUTE_CLASSIFICATIONS.UNKNOWN } as const;
  }
  if (profile !== DEPLOYMENT_PROFILES.WWW_CANDIDATE) {
    throw new Error(`Unknown deployment profile: ${profile}`);
  }

  if (pathname === "/" || pathname === "/public" || pathname.startsWith("/public/")) {
    return { kind: ROUTE_CLASSIFICATIONS.MARKETING } as const;
  }
  if (isRetiredCandidatePath(pathname)) return { kind: ROUTE_CLASSIFICATIONS.RETIRED } as const;
  const route = findCandidateProductRoute(pathname);
  if (route) return { kind: ROUTE_CLASSIFICATIONS.PRODUCT, routeId: route.id } as const;
  return { kind: ROUTE_CLASSIFICATIONS.UNKNOWN } as const;
}
